// Runs the client tool calls of a batch (PLAN-agent-v2 #12): dispatch `tool_name → executor`,
// input guard, journal recording (#7), sequential execution (Office.js writes must not
// interleave), cancellation. Host-agnostic: the executor is injected and mocked in tests.
import type { ClientToolCall, ToolResult } from '../../types/agent';
import type { AuditDeps, CaptureMeta, CaptureRow, CaptureUploadResponse } from '../../types/audit';
import { changeJournal as defaultJournal, entrySnapshots, type ChangeJournal, type JournalEntry, type RangeSnapshot } from './ChangeJournal';
import { TOOL_CATALOGUE, isAnsweredTool, isServerTool, toolTarget } from './toolCatalogue';
import type { ExcelToolExecutor } from '../excel/ExcelToolExecutor';
import { excelToolExecutor } from '../excel/ExcelToolExecutor';
import { isToolError } from '../excel/officeErrors';
import { cellCount, copyDestination, isValidAddress, parseRange, rangeToA1, parseAreas } from '../excel/a1';
import { layoutBlock, validateBlock } from '../excel/block';
import { buildReceipt, CAPTURE_PAGE_ROWS, chunkCapture } from '../excel/capture';
import { AUDIT_SHEET_DEFAULT_NAME } from '../excel/auditSheet';
import { getWorkbookId } from '../excel/workbookIdentity';
import { apiService } from '../ApiService';
import type { InsertModelInput, OverwritePreview, TemplateFiller, WriteBlockInput } from '../../types/excel';

export interface ToolExecution {
  tool_call_id: string;
  tool_name: string;
  result: ToolResult;
  /** Journal entry created for this call (undoable), if any. */
  journal_id: string | null;
  target: { sheet: string; address: string } | null;
  duration_ms: number;
}

export interface RunCallbacks {
  onStart?: (call: ClientToolCall) => void;
  onDone?: (exec: ToolExecution) => void;
}

type Handler = (input: Record<string, unknown>) => Promise<ToolResult>;

export const CANCELLED_RESULT: ToolResult = { status: 'cancelled', reason: 'user' };
export const ABORTED_RESULT: ToolResult = { status: 'cancelled', reason: 'aborted' };
/** A call of a `stop_on_error` batch after one that failed: not run (sculpt-api PLAN-operations-fixes.md point 10). */
export const STOPPED_RESULT: ToolResult = { status: 'cancelled', reason: 'stopped', message: 'not run: an earlier call of the batch failed' };

const NOT_CONFIGURED: ToolResult = { error: 'Audit tools are not configured in this add-in build (no API client).', code: 'NotConfigured', retryable: false, hint: 'Tell the user the capture / audit-sheet tools are unavailable here.' };

/** Largest before-image captured for a scattered write: past it the bounding box says little about the
 *  edit and the snapshot costs more than the Undo is worth. */
const JOURNAL_MAX_CELLS = 20_000;

export class ToolRunner {
  private readonly handlers: Record<string, Handler>;

  constructor(
    private readonly executor: ExcelToolExecutor = excelToolExecutor,
    private readonly journal: ChangeJournal = defaultJournal,
    /** Capture upload + audit report + workbook id (AUDIT_SPEC §2.4); absent → the audit tools answer NotConfigured. */
    private readonly audit?: AuditDeps,
    /** Fetches a filled template for `excel_insert_model`; absent → the tool answers NotConfigured. */
    private readonly fillTemplate?: TemplateFiller,
  ) {
    const ex = executor as unknown as Record<string, unknown>;
    this.handlers = {};
    for (const name of Object.keys(TOOL_CATALOGUE)) {
      if (isServerTool(name) || isAnsweredTool(name)) continue; // neither reaches the client executor
      const fn = ex[name];
      if (typeof fn === 'function') this.handlers[name] = (input) => (fn as Handler).call(executor, input);
    }
    // Journal-backed tools are implemented here, not in the executor.
    this.handlers.excel_undo_last_change = (input) => this.undo(input);
    this.handlers.excel_get_change_journal = async (input) => ({ entries: this.journal.list(Number(input.last_n) || 20).map(publicEntry) });
    // Audit tools compose Office.js reads with HTTP (the payload never travels through /resume).
    this.handlers.excel_capture_sheet = (input) => this.captureSheet(input);
    this.handlers.excel_write_audit_sheet = (input) => this.writeAuditSheet(input);
    // A new model: the executor runs Office.js, the filled file comes over HTTP (never through /resume).
    this.handlers.excel_insert_model = (input) => this.executor.excel_insert_model(input as unknown as InsertModelInput, this.fillTemplate);
  }

  hasTool(name: string): boolean {
    return name in this.handlers;
  }

  toolNames(): string[] {
    return Object.keys(this.handlers);
  }

  /** Execute one call: journal (if applicable) → handler → normalised result. Never throws. */
  async run(call: ClientToolCall): Promise<ToolExecution> {
    const t0 = Date.now();
    const handler = this.handlers[call.tool_name];
    const target = toolTarget(call.tool_name, call.input ?? {});
    if (!handler) {
      return {
        tool_call_id: call.tool_call_id,
        tool_name: call.tool_name,
        result: { error: `Unknown client tool: ${call.tool_name}`, code: 'UnknownTool', retryable: false, hint: 'Use only the tools listed in your tool definitions.' },
        journal_id: null,
        target,
        duration_ms: Date.now() - t0,
      };
    }
    if (call.input !== undefined && (typeof call.input !== 'object' || call.input === null || Array.isArray(call.input))) {
      return {
        tool_call_id: call.tool_call_id,
        tool_name: call.tool_name,
        result: { error: 'Tool input must be a JSON object', code: 'InvalidInput', retryable: true, hint: 'Pass the parameters as an object.' },
        journal_id: null,
        target,
        duration_ms: Date.now() - t0,
      };
    }
    const input = call.input ?? {};
    let journalId: string | null = null;
    const journalRange = this.journalRangeFor(call.tool_name, input);
    if (journalRange) {
      try {
        // Multi-area addresses ("D13,D42,D43") snapshot every area; they are restored together.
        const areas = journalRange.address.includes(',') ? parseAreas(journalRange.address) : [journalRange.address];
        const snaps: RangeSnapshot[] = [];
        for (const a of areas) snaps.push(await this.executor.snapshotRange(journalRange.sheet, a));
        journalId = this.journal.record({ tool_name: call.tool_name, tool_call_id: call.tool_call_id, summary: call.preview || call.tool_name, before: snaps[0], extra: snaps.length > 1 ? snaps.slice(1) : undefined }).id;
      } catch (err) {
        // A failed snapshot must not block the write; the card just won't offer Undo.
        console.warn('[agent-v2] journal snapshot failed', err);
      }
    }
    let result: ToolResult;
    try {
      result = await handler(input);
    } catch (err) {
      result = { error: err instanceof Error ? err.message : String(err), code: 'RuntimeError', retryable: true, hint: 'Retry once; if it recurs, explain the error to the user.' };
    }
    if (journalId && isToolError(result)) {
      // Nothing was written: drop the journal entry so Undo doesn't restore a no-op.
      this.journal.markUndone(journalId);
      journalId = null;
    }
    if (!isToolError(result)) {
      // Keep the journal coherent with sheet-level changes: a deleted sheet can never be restored
      // (entries invalidated), a renamed one keeps its entries under the new name.
      if (call.tool_name === 'excel_delete_sheet' && typeof input.name === 'string') this.journal.invalidateSheet(input.name);
      if (call.tool_name === 'excel_rename_sheet' && typeof input.sheet === 'string' && typeof input.new_name === 'string') this.journal.renameSheet(input.sheet, input.new_name);
    }
    const changes = (result as Record<string, unknown>)._changes as { sheet?: string; address?: string } | undefined;
    const finalTarget = changes?.sheet && changes?.address ? { sheet: changes.sheet, address: changes.address } : target;
    return { tool_call_id: call.tool_call_id, tool_name: call.tool_name, result, journal_id: journalId, target: finalTarget, duration_ms: Date.now() - t0 };
  }

  /** Execute calls one after the other, in order. An aborted signal cancels the remaining calls
   *  (their results are `aborted` so the batch can still be posted and the history stays valid). With
   *  `stopOnError`, a failed call stops the batch: the calls after it are answered `stopped`, never run — an
   *  operation's writes are in the coordinates its insertions make, and a write after a failed insertion lands on
   *  the lines beside (sculpt-api PLAN-operations-fixes.md point 10). */
  async runSequential(calls: ClientToolCall[], signal?: AbortSignal, cb: RunCallbacks = {}, opts: { stopOnError?: boolean } = {}): Promise<ToolExecution[]> {
    const out: ToolExecution[] = [];
    let stopped = false;
    for (const call of calls) {
      if (signal?.aborted) {
        out.push({ tool_call_id: call.tool_call_id, tool_name: call.tool_name, result: ABORTED_RESULT, journal_id: null, target: null, duration_ms: 0 });
        continue;
      }
      if (stopped) {
        const exec: ToolExecution = { tool_call_id: call.tool_call_id, tool_name: call.tool_name, result: STOPPED_RESULT, journal_id: null, target: null, duration_ms: 0 };
        out.push(exec);
        cb.onDone?.(exec);
        continue;
      }
      cb.onStart?.(call);
      const exec = await this.run(call);
      out.push(exec);
      cb.onDone?.(exec);
      if (opts.stopOnError && isToolError(exec.result)) stopped = true;
    }
    return out;
  }

  /** Undo the `count` most recent journalled changes (newest first). Entries whose sheet no
   *  longer exists are skipped (and marked undone) instead of failing the whole undo. */
  async undo(input: Record<string, unknown>): Promise<ToolResult> {
    const count = Math.max(1, Math.min(20, Number(input.count) || 1));
    const candidates = this.journal.undoCandidates(count);
    if (!candidates.length) return { status: 'nothing_to_undo', restored: [], skipped: [] };
    const restored: string[] = [];
    const skipped: string[] = [];
    let last: { sheet: string; address: string } | null = null;
    for (const entry of candidates) {
      const label = `${entry.before.sheet}!${entry.before.address}`;
      if (!(await this.sheetStillExists(entry.before.sheet))) {
        this.journal.markUndone(entry.id, true);
        skipped.push(label);
        continue;
      }
      try {
        for (const snap of entrySnapshots(entry)) await this.executor.restoreSnapshot(snap);
        this.journal.markUndone(entry.id);
        restored.push(label);
        last = entry.before;
      } catch (err) {
        if (isOfficeCode(err, 'ItemNotFound')) {
          this.journal.markUndone(entry.id, true);
          skipped.push(label);
          continue;
        }
        return { error: `Undo failed at ${label}: ${err instanceof Error ? err.message : String(err)}`, code: 'UndoFailed', retryable: false, hint: 'Some changes may already be reverted; inspect the sheet.', restored, skipped };
      }
    }
    const out: ToolResult = { status: 'ok', restored, skipped };
    if (last) out._changes = { sheet: last.sheet, address: last.address, cells: 0 };
    return out;
  }

  /** The in-memory journal entry of a call (for the persisted write-through, domain #38f). */
  journalEntry(journalId: string): JournalEntry | undefined {
    return this.journal.get(journalId);
  }

  /** Restore a before-image fetched from the server (`GET /agent-v2/undo`) after a pane reload, when
   *  the in-memory journal no longer has it. The caller checks `stale` first; here we only refuse
   *  when the sheet is gone. */
  async restoreFromRecord(record: { sheet: string; before_image: Record<string, unknown> }): Promise<ToolResult> {
    const snaps = (record.before_image.snapshots as RangeSnapshot[] | undefined) ?? [];
    if (!snaps.length) return { status: 'nothing_to_undo', restored: [], skipped: [] };
    if (!(await this.sheetStillExists(record.sheet))) return { status: 'ok', restored: [], skipped: [record.sheet] };
    try {
      for (const snap of snaps) await this.executor.restoreSnapshot(snap);
      return { status: 'ok', restored: snaps.map((s) => `${s.sheet}!${s.address}`), skipped: [] };
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err), code: 'UndoFailed', retryable: false, hint: '' };
    }
  }

  /** Undo one specific journal entry (card "Undo" button). */
  async undoEntry(journalId: string): Promise<ToolResult> {
    const entry = this.journal.get(journalId);
    if (!entry || entry.undone) return { status: 'nothing_to_undo', restored: [], skipped: [] };
    const label = `${entry.before.sheet}!${entry.before.address}`;
    if (!(await this.sheetStillExists(entry.before.sheet))) {
      this.journal.markUndone(entry.id, true);
      return { status: 'ok', restored: [], skipped: [label] };
    }
    try {
      for (const snap of entrySnapshots(entry)) await this.executor.restoreSnapshot(snap);
      this.journal.markUndone(entry.id);
      return { status: 'ok', restored: [label], skipped: [] };
    } catch (err) {
      return { error: err instanceof Error ? err.message : String(err), code: 'UndoFailed', retryable: false, hint: '' };
    }
  }

  /** `excel_capture_sheet` (AUDIT_SPEC §2.4): read the sheet page by page through the executor, chunk the
   *  rows under the JSON budget, upload the chunks sequentially to `POST /agent-v2/captures` (chunk 0
   *  mints the `capture_id`) and return the compact receipt. Any upload failure → CaptureUploadFailed. */
  async captureSheet(input: Record<string, unknown>): Promise<ToolResult> {
    if (!this.audit) return NOT_CONFIGURED;
    const sheet = typeof input.sheet === 'string' ? input.sheet : '';
    if (!sheet) return { error: 'Invalid input: sheet is required', code: 'InvalidInput', retryable: true, hint: 'Pass the exact worksheet name.' };
    const rowFrom = Math.max(1, Math.floor(Number(input.row_from) || 1));
    const maxRows = input.max_rows == null || !Number.isFinite(Number(input.max_rows)) || Number(input.max_rows) < 1 ? null : Math.floor(Number(input.max_rows));
    const uploadFailed = (err: unknown): ToolResult => ({
      error: `Capture upload failed: ${err instanceof Error ? err.message : String(err)}`,
      code: 'CaptureUploadFailed',
      retryable: true,
      hint: 'Retry once; if it recurs, fall back to excel_scan_sheet / excel_read_range on the rows you need.',
    });
    let workbookId: string | null = null;
    try {
      workbookId = await this.audit.getWorkbookId();
    } catch {
      workbookId = null;
    }
    if (!workbookId) return uploadFailed('the workbook has no id yet (send a first message so the add-in can create it)');

    // 1. Pages through the executor (one Excel.run each).
    let meta: CaptureMeta | null = null;
    const rows: CaptureRow[] = [];
    let cursor = rowFrom;
    let remaining = maxRows;
    for (;;) {
      const pageRows = remaining === null ? CAPTURE_PAGE_ROWS : Math.min(CAPTURE_PAGE_ROWS, remaining);
      if (pageRows < 1) break;
      const page = await this.executor.readSheetCapture(sheet, cursor, pageRows, meta === null);
      if (isToolError(page)) return page;
      if (page.meta) meta = page.meta;
      rows.push(...page.rows);
      if (remaining !== null) remaining -= page.rows.length;
      if (!page.rows.length || page.row_to >= page.last_row) break;
      cursor = page.row_to + 1;
    }
    if (!meta) return uploadFailed('the sheet could not be read');
    meta = { ...meta, row_from: rows.length ? rows[0].n : meta.row_from, row_to: rows.length ? rows[rows.length - 1].n : meta.row_from - 1, rows: rows.length };
    let fingerprint: string | null = null;
    try {
      fingerprint = await this.executor.readSheetFingerprint(sheet);
    } catch {
      fingerprint = null;
    }

    // 2. Chunks → sequential upload; the first response gives the capture id for the rest.
    const chunks = chunkCapture({ workbook_id: workbookId, sheet, fingerprint, meta, rows });
    let last: CaptureUploadResponse | null = null;
    try {
      for (const chunk of chunks) {
        if (last) chunk.capture_id = last.capture_id;
        last = await this.audit.uploadCaptureChunk(chunk);
        if (!last || typeof last.capture_id !== 'string' || !last.capture_id) throw new Error('the server returned no capture_id');
      }
    } catch (err) {
      return uploadFailed(err);
    }
    return buildReceipt(meta, last as CaptureUploadResponse, chunks) as unknown as ToolResult;
  }

  /** `excel_write_audit_sheet`: fetch the report of a run and let the executor write it (findings never
   *  cross the model to reach Excel). */
  async writeAuditSheet(input: Record<string, unknown>): Promise<ToolResult> {
    if (!this.audit) return NOT_CONFIGURED;
    const runId = typeof input.run_id === 'string' ? input.run_id.trim() : '';
    if (!runId) return { error: 'Invalid input: run_id is required', code: 'InvalidInput', retryable: true, hint: 'Pass the audit run id (audit_run_update / the map audit pointer).' };
    let report;
    try {
      report = await this.audit.getAuditReport(runId);
    } catch (err) {
      return { error: `Audit report unavailable: ${err instanceof Error ? err.message : String(err)}`, code: 'ApiError', retryable: true, hint: 'Check the run id; retry once. If it recurs, report the findings in the chat instead.' };
    }
    const sheetName = typeof input.sheet_name === 'string' && input.sheet_name.trim() ? input.sheet_name.trim() : AUDIT_SHEET_DEFAULT_NAME;
    return this.executor.excel_write_audit_sheet_rows({ sheet_name: sheetName, replace: input.replace !== false, report });
  }

  /** Best effort: a missing `sheetExists` on the executor (older mocks) counts as "exists". */
  private async sheetStillExists(sheet: string): Promise<boolean> {
    try {
      return await this.executor.sheetExists(sheet);
    } catch {
      return true;
    }
  }

  /** Range whose before-image is captured for a write tool, or null when not journalled. */
  /** Read-only pre-flight for calls whose card needs extra facts before approval (domain #36: the
   *  overwrite acknowledgement of `excel_write_block`). Null when the tool has no preview. */
  async previewCall(call: ClientToolCall): Promise<OverwritePreview | null> {
    if (call.tool_name !== 'excel_write_block') return null;
    const fn = (this.executor as unknown as { previewWriteBlock?: (i: WriteBlockInput) => Promise<OverwritePreview> }).previewWriteBlock;
    if (typeof fn !== 'function') return null;
    return fn.call(this.executor, call.input as unknown as WriteBlockInput);
  }

  private journalRangeFor(toolName: string, input: Record<string, unknown>): { sheet: string; address: string } | null {
    if (toolName === 'excel_write_block') {
      // The rectangle is derived from anchor + rows + axis; inserted rows are not undoable by restore.
      if (input.mode === 'insert_rows') return null;
      try {
        const block = input as unknown as WriteBlockInput;
        if (validateBlock(block).length) return null;
        return { sheet: String(input.sheet), address: layoutBlock(block).targetAddress };
      } catch {
        return null;
      }
    }
    if (toolName === 'excel_write_cells') {
      // Scattered cells: the before-image is their bounding box — one snapshot instead of up to 200,
      // and a comma address would exceed MAX_AREAS. Restoring the box is correct: every cell in it was
      // captured before the write. A box far larger than the edit is not worth capturing, and the card
      // then simply offers no Undo.
      const target = toolTarget(toolName, input);
      if (!target || !isValidAddress(target.address)) return null;
      return cellCount(target.address) > JOURNAL_MAX_CELLS ? null : target;
    }
    const meta = TOOL_CATALOGUE[toolName];
    if (!meta?.journalled) {
      if (toolName === 'excel_copy_range') return copyDestinationOf(input);
      return null;
    }
    const target = toolTarget(toolName, input);
    if (!target) return null;
    if (target.address.includes(',')) {
      try {
        return { sheet: target.sheet, address: parseAreas(target.address).join(',') };
      } catch {
        return null;
      }
    }
    if (!isValidAddress(target.address)) return null;
    return { sheet: target.sheet, address: rangeToA1(parseRange(target.address)) };
  }
}

/** Destination range of a copy = dest anchor + source shape (transposed if asked). */
function copyDestinationOf(input: Record<string, unknown>): { sheet: string; address: string } | null {
  const sheet = input.dest_sheet;
  const source = input.source;
  const dest = input.dest;
  if (typeof sheet !== 'string' || typeof source !== 'string' || typeof dest !== 'string') return null;
  if (!isValidAddress(source) || !isValidAddress(dest)) return null;
  return { sheet, address: copyDestination(source, dest, !!input.transpose) };
}

function isOfficeCode(err: unknown, code: string): boolean {
  return typeof err === 'object' && err !== null && (err as { code?: unknown }).code === code;
}

function publicEntry(e: JournalEntry) {
  return { id: e.id, tool: e.tool_name, summary: e.summary, sheet: e.before.sheet, address: e.before.address, timestamp: e.timestamp, undone: e.undone, invalidated: e.invalidated ?? false };
}

/** Production runner: the real executor, the shared journal, the audit deps (API client + workbook id) and the
 *  template fill of `excel_insert_model`. */
export const toolRunner = new ToolRunner(excelToolExecutor, defaultJournal, {
  uploadCaptureChunk: (body) => apiService.uploadCaptureChunk(body),
  getAuditReport: (runId) => apiService.getAuditReport(runId),
  getWorkbookId,
}, (template, technology, inputs) => apiService.fillTemplate(template, technology, inputs));
